import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ApiError, api, type Uuid } from "@shared/api";
import { Dialog } from "@shared/ui/bits";
import { Button } from "@shared/ui/button";
import { Textarea } from "@shared/ui/textarea";
import { Card } from "@shared/ui/settings-card";
import { IconRules } from "@shared/ui/icons";

const RULES_MAX = 2000;

/**
 * Правила сервера в настройках — как «проверка участников» в Discord. Касаются
 * пришедших после того, как правила завели: старожилов они не запирают.
 */
export function RulesTab({
  serverId,
  rules,
  onChanged,
}: {
  serverId: Uuid;
  rules: string | null;
  onChanged: () => void;
}) {
  const [draft, setDraft] = useState(rules ?? "");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => setDraft(rules ?? ""), [rules]);

  async function save() {
    setBusy(true);
    setNote(null);
    try {
      await api.servers.update(serverId, { rules: draft.trim() || null });
      onChanged();
      setNote({ ok: true, text: draft.trim() ? "Правила сохранены" : "Правила сняты" });
    } catch (error) {
      setNote({ ok: false, text: error instanceof ApiError ? error.message : "Не сохранилось" });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="automod">
      <Card title="Правила сервера">
        <div className="automod-words">
          <Textarea
            value={draft}
            rows={10}
            maxLength={RULES_MAX}
            aria-label="Правила сервера"
            placeholder={"1. Уважайте друг друга.\n2. Без спама."}
            onChange={(event) => setDraft(event.target.value)}
          />
          <div className="small muted">
            Кто придёт на сервер после того, как правила появились, сначала примет их — до этого он только
            читает. Тех, кто уже здесь, правила не запирают. Пусто — правил нет.
          </div>
        </div>
      </Card>
      <div className="line" style={{ gap: 10, alignItems: "center", justifyContent: "flex-end" }}>
        {note && <span className={`small ${note.ok ? "muted" : "danger-text"}`}>{note.text}</span>}
        <Button type="button" disabled={busy || draft.trim() === (rules ?? "")} onClick={() => void save()}>
          Сохранить
        </Button>
      </div>
    </div>
  );
}

/** Полоса вместо поля ввода у того, кто ещё не принял правила сервера. */
export function RulesBar({ serverId, rules }: { serverId: Uuid; rules: string | null }) {
  const cache = useQueryClient();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  async function accept() {
    setBusy(true);
    setProblem(null);
    try {
      await api.servers.acceptRules(serverId);
      await cache.invalidateQueries({ queryKey: ["rights"] });
      setOpen(false);
    } catch (error) {
      setProblem(error instanceof ApiError ? error.message : "Не получилось");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="rules-bar">
        <IconRules size={16} />
        <span className="grow">Чтобы писать здесь, примите правила сервера.</span>
        <Button type="button" size="sm" onClick={() => setOpen(true)}>
          Прочитать правила
        </Button>
      </div>
      {open && (
        <Dialog
          title="Правила сервера"
          onClose={() => setOpen(false)}
          actions={
            <>
              <Button type="button" variant="secondary" onClick={() => setOpen(false)}>
                Позже
              </Button>
              <Button type="button" disabled={busy} onClick={() => void accept()}>
                Принимаю
              </Button>
            </>
          }
        >
          <div className="rules-text">{rules ?? "Правила недоступны."}</div>
          {problem && <div className="small danger-text">{problem}</div>}
        </Dialog>
      )}
    </>
  );
}
